'use client';

import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { fadeIn } from '@/lib/animations';
import { Content, Markdown } from '@/lib/markdown';

export interface FAQ {
	question: string;
	answer: string | Content;
}

export interface FrequentlyAskedQuestionsProps {
	className?: string;
	title?: string;
	subtitle?: string;
	description?: string | Content;
	faqs: FAQ[];
}

export default function FrequentlyAskedQuestions(props: FrequentlyAskedQuestionsProps) {
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	const toggleFAQ = (index: number) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	const onFAQItemActivate = (index: number, e: MouseEvent<HTMLElement>) => {
		if ((e.target as HTMLElement).closest('a')) return;
		toggleFAQ(index);
	};

	const onFAQItemKeyDown = (index: number, e: KeyboardEvent<HTMLElement>) => {
		if (e.key !== 'Enter' && e.key !== ' ') return;
		e.preventDefault();
		toggleFAQ(index);
	};

	return (
		<section className={`py-24 bg-white ${props.className || ''}`}>
			<div className="container mx-auto px-4 md:px-6">
				{(props.subtitle || props.title || props.description) && (
					<motion.div
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, amount: 0.3 }}
						variants={fadeIn('up')}
						className="mb-4 lg:mb-8"
					>
						{props.subtitle && (
							<h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
								{props.subtitle}
							</h2>
						)}
						{props.title && (
							<h3 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-[#1A335B] ">
								{props.title}
							</h3>
						)}
						{props.description && (
							<div className="mx-auto text-gray-700">
								{typeof props.description === 'string' ? (
									<p>{props.description}</p>
								) : (
									<Markdown content={props.description} />
								)}
							</div>
						)}
					</motion.div>
				)}

				<motion.div
					initial="hidden"
					whileInView="show"
					viewport={{ once: true, amount: 0.3 }}
					variants={fadeIn('up')}
					className=" mx-auto"
				>
					<div className="grid grid-cols-1 items-start">
						{props.faqs?.map((faq, index) => (
							<motion.div
								key={index}
								variants={fadeIn('up', 0.05 * index)}
								className="bg-white border-b border-[#CCCCCC] overflow-hidden cursor-pointer outline-none transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1A335B]"
								role="button"
								tabIndex={0}
								aria-expanded={openIndex === index}
								aria-labelledby={`faq-question-${index}`}
								onClick={(e) => onFAQItemActivate(index, e)}
								onKeyDown={(e) => onFAQItemKeyDown(index, e)}
							>
								<div className="w-full p-4 text-left flex items-center justify-between">
									<h4
										id={`faq-question-${index}`}
										className="text-lg font-bold text-[#1A335B] pr-4"
									>
										{faq.question}
									</h4>
									<motion.div
										className="border border-[#CCCCCC] rounded-sm min-w-[40px] min-h-[40px] flex items-center justify-center pointer-events-none"
										animate={{ rotate: openIndex === index ? 180 : 0 }}
										transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
									>
										<ArrowDown
											className="text-[#0A0A0A] flex-shrink-0"
											size={16}
										/>
									</motion.div>
								</div>

								<motion.div
									initial={false}
									animate={{
										height: openIndex === index ? 'auto' : 0,
										opacity: openIndex === index ? 1 : 0,
									}}
									transition={{
										height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
										opacity: { duration: 0.2, ease: 'easeInOut' },
									}}
									style={{ overflow: 'hidden' }}
								>
									<div className="px-4 pb-4 pt-1">
										{typeof faq.answer === 'string' ? (
											<p className="text-[#0A0A0A] leading-relaxed">
												{faq.answer}
											</p>
										) : (
											<div className="text-[#0A0A0A] leading-relaxed">
												<Markdown content={faq.answer} />
											</div>
										)}
									</div>
								</motion.div>
							</motion.div>
						))}
					</div>
				</motion.div>
			</div>
		</section>
	);
}
